import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Navbar.css';
import AuthModal from './AuthModal';

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [user, setUser] = useState<any>(null);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (path: string) => {
    setMobileMenuOpen(false); // Close mobile menu
    if (path.startsWith('#')) {
      // Handle anchor links for same-page navigation
      const element = document.querySelector(path);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Handle route navigation
      navigate(path);
    }
  };

  const handleCoursesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false); // Close mobile menu
    // If we're not on the home page, navigate to home first then scroll
    if (window.location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.querySelector('#courses');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If we're already on home page, just scroll
      const element = document.querySelector('#courses');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const openAuthModal = (mode: 'login' | 'signup') => {
    setAuthMode(mode);
    setAuthModalOpen(true);
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('authToken');
    
    if (token) {
      try {
        await fetch('http://localhost:5000/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  return (
    <>
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <div className="nav-brand">
          <Link to="/" className="brand-link">
            <img 
              src="/images/GS_logo.png" 
              alt="Global Sansys Logo" 
              className="brand-logo"
            />
            <span className="brand-text">Global Sansys</span>
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* Navigation Links */}
        <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <Link to="/" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Home</Link>
          
          {/* Products Dropdown */}
          <div 
            className="nav-dropdown"
            onMouseEnter={() => setProductsDropdownOpen(true)}
            onMouseLeave={() => setProductsDropdownOpen(false)}
          >
            <span className="nav-link">
              Products <span className="dropdown-arrow">▼</span>
            </span>
            {productsDropdownOpen && (
              <div className="dropdown-menu">
                <Link 
                  to="/products/apex-intelligence" 
                  className="dropdown-item"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    setProductsDropdownOpen(false);
                  }}
                >
                  <div className="dropdown-item-icon">
                    <img src="/images/APEX_ICON.png" alt="APEX Intelligence" className="dropdown-icon-img" />
                  </div>
                  <div className="dropdown-item-content">
                    <div className="dropdown-item-title">APEX Intelligence</div>
                    <div className="dropdown-item-desc">Institutional-grade trading intelligence</div>
                  </div>
                </Link>
              </div>
            )}
          </div>
          
          <Link to="/pilot-program" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Pilot Program</Link>
          <Link to="/careers" className="nav-link" onClick={() => setMobileMenuOpen(false)}>Careers</Link>
          <a href="#courses" className="nav-link" onClick={handleCoursesClick}>Courses</a>
          <a href="#about" className="nav-link" onClick={(e) => { e.preventDefault(); handleNavClick('#about'); }}>About Us</a>
          <a href="#contact" className="nav-link" onClick={(e) => { e.preventDefault(); handleNavClick('#contact'); }}>Contact</a>
          
          {user ? (
            <>
              <Link to="/dashboard" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                Dashboard
              </Link>
              <div className="user-menu">
                {user.profile_picture && (
                  <img src={user.profile_picture} alt={user.full_name} className="user-avatar-nav" />
                )}
                <span className="user-name">{user.full_name}</span>
                <button className="btn-logout" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </>
          ) : (
            <>
              <button 
                className="btn-login"
                onClick={() => openAuthModal('login')}
              >
                Log In
              </button>
              
              <button 
                className="btn-join-free"
                onClick={() => openAuthModal('signup')}
              >
                Join for Free
              </button>
            </>
          )}
        </div>

        {/* Mobile Overlay */}
        {mobileMenuOpen && (
          <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
        )}
      </div>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authMode}
      />
    </nav>
    </>
  );
};

export default Navbar;